/**
 * Seeded, named, independent random streams (AD-007a). One mission seed splits
 * into a fixed set of streams so that consuming randomness in one subsystem can
 * never perturb another — a long burst of gun-jam rolls must not change which
 * wing fails in a dive. `Math.random` is banned inside the sim.
 *
 * The generator is xoshiro128** over four uint32 words: integer-only (so it is
 * bit-identical anywhere JavaScript runs), cheap, and trivially serializable as
 * four plain numbers.
 */

import { fnv1a32 } from "./hash.ts";

/**
 * The stream set is fixed and named up front rather than created on demand:
 * adding a stream later must be a deliberate, reviewable change, because a
 * stream that appears mid-development shifts every seed's behaviour.
 * `weather` is reserved — nothing consumes it yet (wind is not in the data at
 * all, see flight-tunables.md §8).
 *
 * `gunnery` and `ai` are AD-007g's combat pair, added when the encounter layer
 * landed: every damaged-airframe gun jitter and hit allocation draws from
 * `gunnery`, every AI manoeuvre choice from `ai`, so a dogfight cannot perturb
 * the airframe's own jam/ice/overstress rolls. They are drawn from the
 * *encounter's* bank rather than any one aircraft's (see `combat/encounter.ts`);
 * an aircraft's own bank carries them untouched.
 *
 * `flak` is the ground-fire pair to `gunnery` (AD-010): every battery's target
 * acquisition, reload jitter, burst scatter, hit roll and hit allocation draws
 * from it, so a balloon site's archie cannot move which wing a dogfight over it
 * takes off next. It lives on the *encounter's* bank for the same reason
 * `gunnery` does — batteries belong to the encounter, not to any aeroplane.
 *
 * `mission` is drawn only at *setup* time, by the mission generators in
 * `mission/` — which aeroplanes are in the sky, where they start, what the
 * weather is. Nothing consumes it during a tick, so a generated mission and a
 * hand-written encounter run the same physics from the same seed. Adding it was
 * safe against every existing seed because each stream's starting position is
 * `fnv1a32(name, missionSeed)`: a new name cannot move an old stream.
 *
 * `airship` is the Zeppelin's defensive fire (`combat/airship-pass.ts`): the
 * graze scatter on every round its four gunners put up, and the magnitude and
 * component allocation of every hit those rounds score. It is its own stream on
 * exactly the precedent `flak` set — an airship is not an aeroplane, it belongs
 * to the encounter rather than to any flight, and a gasbag emptying a belt into
 * a wingman must not move which wing the dogfight beside it takes off next.
 * Reusing `gunnery` would have avoided a snapshot bump and bought that saving
 * with the one property AD-007a exists to protect.
 */
export const RNG_STREAM_NAMES = [
  "gunJam",
  "carbIce",
  "damage",
  "gunnery",
  "ai",
  "weather",
  "mission",
  "flak",
  "airship",
] as const;

export type RngStreamName = (typeof RNG_STREAM_NAMES)[number];

/** One stream's serializable position: the four xoshiro128** words. */
export interface RngStreamState {
  readonly s0: number;
  readonly s1: number;
  readonly s2: number;
  readonly s3: number;
}

export type RngBankState = Readonly<Record<RngStreamName, RngStreamState>>;

function splitmix32(input: number): number {
  let z = (input + 0x9e3779b9) | 0;
  z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
  z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
  return (z ^ (z >>> 15)) >>> 0;
}

function seedStream(missionSeed: number, name: RngStreamName): RngStreamState {
  const mixed = fnv1a32(name, missionSeed >>> 0);
  return {
    s0: splitmix32(mixed),
    s1: splitmix32(mixed ^ 0x9e3779b9),
    s2: splitmix32(mixed ^ 0x85ebca6b),
    s3: splitmix32(mixed ^ 0xc2b2ae35),
  };
}

/** Builds one record per named stream, so adding a stream is a one-line change here. */
function perStream<T>(make: (name: RngStreamName) => T): Record<RngStreamName, T> {
  const entries = RNG_STREAM_NAMES.map((name) => [name, make(name)] as const);
  return Object.fromEntries(entries) as Record<RngStreamName, T>;
}

/** Derives every stream's starting position from one mission seed. */
export function createRngBankState(missionSeed: number): RngBankState {
  return perStream((name) => seedStream(missionSeed, name));
}

function rotl32(value: number, bits: number): number {
  return ((value << bits) | (value >>> (32 - bits))) >>> 0;
}

interface MutableStream {
  s0: number;
  s1: number;
  s2: number;
  s3: number;
}

function advance(stream: MutableStream): number {
  const output = Math.imul(rotl32(Math.imul(stream.s1, 5) >>> 0, 7), 9) >>> 0;
  const t = (stream.s1 << 9) >>> 0;
  stream.s2 = (stream.s2 ^ stream.s0) >>> 0;
  stream.s3 = (stream.s3 ^ stream.s1) >>> 0;
  stream.s1 = (stream.s1 ^ stream.s2) >>> 0;
  stream.s0 = (stream.s0 ^ stream.s3) >>> 0;
  stream.s2 = (stream.s2 ^ t) >>> 0;
  stream.s3 = rotl32(stream.s3, 11);
  return output;
}

/**
 * A mutable cursor over the four streams, created from the immutable bank state
 * held in the sim state at the start of a tick and read back out at the end.
 * Streams that nothing draws from during a tick come back byte-identical.
 */
export class RngBank {
  private readonly streams: Record<RngStreamName, MutableStream>;

  constructor(state: RngBankState) {
    this.streams = perStream((name) => ({ ...state[name] }));
  }

  /** Next raw 32-bit draw from one stream. */
  nextUint32(name: RngStreamName): number {
    return advance(this.streams[name]);
  }

  /** Next draw mapped to `[0, 1)` with 32 bits of resolution. */
  nextFloat(name: RngStreamName): number {
    return this.nextUint32(name) / 4294967296;
  }

  /** One Bernoulli trial. `probability <= 0` never draws, keeping disabled subsystems stream-neutral. */
  chance(name: RngStreamName, probability: number): boolean {
    if (probability <= 0) return false;
    if (probability >= 1) return true;
    return this.nextFloat(name) < probability;
  }

  /**
   * Weighted pick over `weights`, returning the chosen index. A non-positive
   * total never draws, so an all-zero row is stream-neutral the way
   * `chance(name, 0)` is.
   */
  weightedIndex(name: RngStreamName, weights: readonly number[]): number {
    const total = weights.reduce((sum, weight) => sum + Math.max(0, weight), 0);
    if (total <= 0) return -1;
    let cursor = this.nextFloat(name) * total;
    for (let index = 0; index < weights.length; index++) {
      cursor -= Math.max(0, weights[index] ?? 0);
      if (cursor < 0) return index;
    }
    return weights.length - 1;
  }

  /** Current position of every stream, for writing back into the sim state. */
  snapshot(): RngBankState {
    return perStream((name) => ({ ...this.streams[name] }));
  }
}
